import { memo, type MouseEvent } from "react";
import { Button, CircularProgress, Stack } from "@mui/material";
import { Close, EventAvailable, VideoCall } from "@mui/icons-material";
import { inviteKindFromTask, isInvitePending } from "../helpers/inviteTaskHelpers";
import { useTeleconsultoriaInviteActions } from "../hooks/useTeleconsultoriaInviteActions";

interface InviteTaskActionsProps {
  task: fhir5.Task;
  /** Fecha painel/diálogo antes de abrir o atendimento (ex.: central de notificações). */
  onBeforeNavigate?: () => void;
  /** Botões compactos (cards da central) ou normais (tela de Teleconsultoria). */
  isCompact?: boolean;
}

/** Os cards ficam dentro de áreas clicáveis: o clique no botão não pode expandir o card. */
function stopPropagation(event: MouseEvent) {
  event.stopPropagation();
}

/**
 * "Aceitar e entrar" / "Recusar" do convite de teleconsultoria.
 * Autocontido (usa o hook do módulo): quem renderiza só passa a Task.
 * Convite já aceito mostra "Entrar na teleconsultoria" (entrar de novo).
 * Reserva com horário ("scheduled"): botão "Aceitar" (não entra no atendimento).
 */
export const InviteTaskActions = memo(function InviteTaskActions({
  task,
  onBeforeNavigate,
  isCompact = false,
}: InviteTaskActionsProps) {
  const { accept, reject, isAccepting, isRejecting } = useTeleconsultoriaInviteActions();
  const isPending = isInvitePending(task);
  const isAccepted = task.status === "accepted";
  // Reserva com horário: aceitar NÃO entra; depois de aceita, o início é pela
  // lista "Minhas teleconsultorias marcadas" (ou dashboard), não pela notificação.
  const isScheduled = inviteKindFromTask(task) === "scheduled";
  if (!isPending && !isAccepted) return null;
  if (isAccepted && isScheduled) return null;

  const isBusy = isAccepting || isRejecting;
  const buttonSx = {
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 1.5,
    ...(isCompact && { fontSize: "0.75rem", minHeight: 30, py: 0.35 }),
  } as const;

  return (
    <Stack direction="row" spacing={1} sx={{ pt: isCompact ? 0.5 : 0 }} onClick={stopPropagation}>
      <Button
        size="small"
        variant="contained"
        disabled={isBusy}
        startIcon={
          isAccepting ? (
            <CircularProgress size={14} color="inherit" />
          ) : isScheduled ? (
            <EventAvailable />
          ) : (
            <VideoCall />
          )
        }
        onClick={(event) => {
          stopPropagation(event);
          void accept(task, onBeforeNavigate);
        }}
        sx={{ ...buttonSx, flex: isCompact ? 1 : undefined }}
      >
        {isAccepted ? "Entrar na teleconsultoria" : isScheduled ? "Aceitar" : "Aceitar e entrar"}
      </Button>
      {isPending && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          disabled={isBusy}
          startIcon={isRejecting ? <CircularProgress size={14} color="inherit" /> : <Close />}
          onClick={(event) => {
            stopPropagation(event);
            void reject(task);
          }}
          sx={buttonSx}
        >
          Recusar
        </Button>
      )}
    </Stack>
  );
});
